/* ═══════════════════════════════════════════════════════════
   CREDIT CONTEXT — AI Credit System (persistent)
   Tracks per-user credit balance via Supabase ai_credits table.
   Free: 20/mo, Trial: 40 total (3 days), Premium: 150/mo.
   Credits are ALWAYS read from / written to the database.
   ═══════════════════════════════════════════════════════════ */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { sbQuery, sbRPC, SB_URL, SB_KEY, sendNotification } from "../config/supabase";

/* ── Default credit costs per action (fallback if DB not set) ── */
const DEFAULT_CREDIT_COSTS = {
  ai_chat: 1,        // Lumi Chat message
  doc_summary: 3,    // Summary generation
  quiz_gen: 4,       // Text quiz generation
  doc_quiz_gen: 5,   // Document quiz generation
  study_plan: 5,     // Study plan generation
};

/* ── Default credit tier allowances ──────────────────────── */
const DEFAULT_CREDIT_TIERS = {
  free:    { credits: 20,  period: "month", label: "Free" },
  trial:   { credits: 40,  period: "3 days", days: 3, label: "Trial" },
  premium: { credits: 150, period: "month", label: "Premium" },
};

/* ── Backward-compat static exports (for direct imports) ── */
export const CREDIT_COSTS = DEFAULT_CREDIT_COSTS;
export const CREDIT_TIERS = DEFAULT_CREDIT_TIERS;

/* ── Purchasable credit packages ─────────────────────────── */
export const CREDIT_PACKAGES = [
  { id: "starter", credits: 50,  slp: 50,  label: "Starter Pack" },
  { id: "value",   credits: 100, slp: 90,  label: "Value Pack", popular: true },
  { id: "pro",     credits: 200, slp: 160, label: "Pro Pack" },
];

const CreditContext = createContext({
  credits: 0,
  creditsUsed: 0,
  monthlyAllowance: 0,
  resetDate: null,
  loading: false,
  loaded: false,
  creditPct: 0,
  creditCosts: DEFAULT_CREDIT_COSTS,
  creditTiers: DEFAULT_CREDIT_TIERS,
  refreshCredits: () => {},
  refreshConfig: () => {},
  deductCredit: () => {},
  purchaseCredits: () => {},
  setCredentials: () => {},
  setPremiumFlag: () => {},
});

export function useCredits() {
  return useContext(CreditContext);
}

export function CreditProvider({ children }) {
  const [credits, setCredits] = useState(0);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [monthlyAllowance, setMonthlyAllowance] = useState(0);
  const [resetDate, setResetDate] = useState(null);
  const [loading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [creditCosts, setCreditCosts] = useState(DEFAULT_CREDIT_COSTS);
  const [creditTiers, setCreditTiers] = useState(DEFAULT_CREDIT_TIERS);
  const tokenRef = useRef(null);
  const userIdRef = useRef(null);
  const isPremiumRef = useRef(false);
  const creditTiersRef = useRef(DEFAULT_CREDIT_TIERS);

  const creditPct = monthlyAllowance > 0
    ? Math.round((credits / monthlyAllowance) * 100)
    : 0;

  // Fetch live config from platform_settings (public read, no auth needed)
  const refreshConfig = useCallback(() => {
    return fetch(`${SB_URL}/rest/v1/platform_settings?key=in.(ai_credit_costs,ai_credit_tiers)&select=key,value`, {
      headers: { apikey: SB_KEY, "Content-Type": "application/json" },
    })
      .then(r => { if (!r.ok) return []; return r.json(); })
      .then(rows => {
        if (!Array.isArray(rows)) return;
        for (const row of rows) {
          try {
            const parsed = JSON.parse(row.value);
            if (row.key === "ai_credit_costs") setCreditCosts({ ...DEFAULT_CREDIT_COSTS, ...parsed });
            if (row.key === "ai_credit_tiers") {
              const merged = { ...DEFAULT_CREDIT_TIERS, ...parsed };
              setCreditTiers(merged);
              creditTiersRef.current = merged;
            }
          } catch {}
        }
      })
      .catch(() => {});
  }, []);

  // Fetch config on mount
  useEffect(() => { refreshConfig(); }, [refreshConfig]);

  /** Apply a credit row from the DB to local state */
  const applyRow = useCallback((row) => {
    setCredits(row.credits_remaining ?? 0);
    setCreditsUsed(row.credits_used ?? 0);
    setMonthlyAllowance(row.monthly_allowance ?? creditTiersRef.current.free.credits);
    setResetDate(row.reset_date ? new Date(row.reset_date) : null);
    setLoaded(true);
  }, []);

  /**
   * Load credits from DB. Calls ensure_ai_credits RPC to auto-create
   * the row if it doesn't exist, then reads the authoritative values.
   */
  const refreshCredits = useCallback(async () => {
    const t = tokenRef.current;
    const u = userIdRef.current;
    if (!t || !u) return;

    // Use premium tier if the user has an active subscription, otherwise free tier
    const isPrem = isPremiumRef.current;
    const tierKey = isPrem ? "premium" : "free";
    const tierCredits = creditTiersRef.current[tierKey]?.credits
      ?? DEFAULT_CREDIT_TIERS[tierKey]?.credits
      ?? DEFAULT_CREDIT_TIERS.free.credits;

    try {
      // 1. Ensure the DB row exists (auto-creates with admin-configured free-tier allowance)
      const ensureResult = await sbRPC("ensure_ai_credits", t, {
        p_user_id: u,
        p_allowance: tierCredits,
      });

      // The RPC returns the current state directly
      if (ensureResult) {
        applyRow(ensureResult);
        return;
      }
    } catch {
      // ensure_ai_credits RPC may not exist yet — try direct query
    }

    // 2. Fallback: read directly from the ai_credits table
    try {
      const rows = await sbQuery("ai_credits", t, "GET", null,
        `?user_id=eq.${u}&select=credits_remaining,credits_used,monthly_allowance,reset_date`);
      const row = rows?.[0];
      if (row) {
        applyRow(row);
        return;
      }
    } catch {
      // Table may not exist
    }

    // 3. Last resort: try to INSERT the row via REST (if RPC doesn't exist but table does)
    try {
      const inserted = await sbQuery("ai_credits", t, "POST", {
        user_id: u,
        credits_remaining: tierCredits,
        credits_used: 0,
        monthly_allowance: tierCredits,
      });
      if (inserted?.[0]) {
        applyRow(inserted[0]);
        return;
      }
    } catch {
      // Row may already exist (conflict) or table missing — use defaults
    }

    // 4. If everything fails, show 0 credits (not fake defaults)
    setCredits(0);
    setCreditsUsed(0);
    setMonthlyAllowance(tierCredits);
    setLoaded(true);
  }, [applyRow]);

  const setCredentials = useCallback((token, userId, { isPremium = false } = {}) => {
    tokenRef.current = token;
    userIdRef.current = userId;
    isPremiumRef.current = isPremium;
    if (token && userId) {
      refreshCredits();
    } else {
      // Logged out — clear state
      setCredits(0);
      setCreditsUsed(0);
      setMonthlyAllowance(0);
      setResetDate(null);
      setLoaded(false);
    }
  }, [refreshCredits]);

  /**
   * Deduct credits — ALWAYS goes through the database RPC.
   * No client-side-only fallback. If DB fails, the deduction fails.
   */
  const deductCredit = useCallback(async (action, amount) => {
    const t = tokenRef.current;
    const u = userIdRef.current;
    if (!t || !u) throw new Error("Not authenticated");

    const cost = amount || creditCosts[action] || 1;
    if (credits < cost) throw new Error("INSUFFICIENT_CREDITS");

    const result = await sbRPC("deduct_ai_credit", t, {
      p_user_id: u,
      p_amount: cost,
      p_action: action,
      p_metadata: {},
    });

    // Update local state from the authoritative DB response
    if (result) {
      setCredits(result.remaining ?? credits - cost);
      setCreditsUsed(result.used ?? creditsUsed + cost);
    }
    return result;
  }, [credits, creditsUsed, creditCosts]);

  const purchaseCredits = useCallback(async (pkg) => {
    const t = tokenRef.current;
    const u = userIdRef.current;
    if (!t || !u) throw new Error("Not authenticated");

    try {
      const result = await sbRPC("purchase_ai_credits", t, {
        p_user_id: u,
        p_credits: pkg.credits,
        p_slp_cost: pkg.slp,
      });
      await refreshCredits();

      // Send in-app notification for credit purchase
      sendNotification(t, {
        userId: u,
        type: "purchase",
        title: `${pkg.credits} AI Credits Added`,
        message: `You purchased the ${pkg.label || "Credit Pack"} for ${pkg.slp} SLP. Your new balance is ready to use.`,
        metadata: { page: "ai-helper" },
      });

      return result;
    } catch (err) {
      if (err.message?.includes("INSUFFICIENT_SLP")) {
        throw new Error("INSUFFICIENT_SLP");
      }
      throw err;
    }
  }, [refreshCredits]);

  /** Update premium flag so refreshCredits uses correct tier allowance */
  const setPremiumFlag = useCallback((flag) => {
    isPremiumRef.current = !!flag;
  }, []);

  return (
    <CreditContext.Provider value={{
      credits, creditsUsed, monthlyAllowance, resetDate, loading, loaded, creditPct,
      creditCosts, creditTiers,
      refreshCredits, refreshConfig, deductCredit, purchaseCredits, setCredentials, setPremiumFlag,
    }}>
      {children}
    </CreditContext.Provider>
  );
}
